import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Loader2, Briefcase, Building2, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { createJob } from "../../api/admin";
import { getFullImageUrl } from "../../utils/imageHandler";


const jobSchema = z.object({
 title: z.string().min(3, "Title must be at least 3 characters"),
 companyName: z.string().min(2, "Company name is required"),
 companyLogo: z.string().url("Invalid URL").optional().or(z.literal("")),
 companyWebsite: z.string().url("Invalid URL").optional().or(z.literal("")),
 location: z.string().min(2, "Location is required"),
 type: z.enum(["FULL_TIME", "PART_TIME", "CONTRACT", "INTERNSHIP"]),
 experience: z.enum(["ENTRY", "MID", "SENIOR", "EXECUTIVE"]),
 salaryMin: z.string().optional(),
 salaryMax: z.string().optional(),
 deadline: z.string().optional(),
 category: z.string().min(2, "Category is required"),
 description: z.string().min(10, "Description must be at least 10 characters"),
 requirements: z.string().optional(),
 tags: z.string().optional(), // Comma separated
});

export default function AdminPostJob() {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);

 const {
 register,
 handleSubmit,
 setValue,
 watch,
 formState: { errors },
 } = useForm({
 resolver: zodResolver(jobSchema),
 defaultValues: {
 type: "FULL_TIME",
 experience: "ENTRY",
 },
 });

 const onSubmit = async (data) => {
 setLoading(true);
 try {
 const payload = {
 ...data,
 tags: data.tags ? data.tags.split(",").map((t) => t.trim()) : [],
 salaryMin: data.salaryMin ? Number(data.salaryMin) : null,
 salaryMax: data.salaryMax ? Number(data.salaryMax) : null,
 };

 await createJob(payload);
 toast.success("Job posted successfully!");
 navigate("/admin/dashboard");
 } catch (err) {
 console.error(err);
 toast.error(err.response?.data?.message || "Failed to post job");
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg">
 <Briefcase className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Post a Job</h1>
 </div>
 <p className="text-muted-foreground">Create a new job listing as Admin</p>
 </div>

 <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
 {/* Company Info */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5" />
 Company Details
 </CardTitle>
 </CardHeader>
 <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label>Company Name</Label>
 <Input {...register("companyName")} placeholder="e.g. Acme Corp" />
 {errors.companyName && (
 <p className="text-sm text-red-500">{errors.companyName.message}</p>
 )}
 </div>
 <div className="space-y-2">
 <Label>Website (Optional)</Label>
 <Input {...register("companyWebsite")} placeholder="https://..." />
 {errors.companyWebsite && (
 <p className="text-sm text-red-500">{errors.companyWebsite.message}</p>
 )}
 </div>
  <div className="md:col-span-2 space-y-2">
 <Label>Logo URL (Optional)</Label>
 <Input {...register("companyLogo")} placeholder="https://..." />
 {errors.companyLogo && (
 <p className="text-sm text-red-500">{errors.companyLogo.message}</p>
 )}
    
    {watch("companyLogo") && (
      <div className="mt-4 p-4 border rounded-2xl bg-muted/30 flex items-center justify-center">
        <div className="relative group">
          <img 
            src={getFullImageUrl(watch("companyLogo"))} 
            alt="Logo Preview" 
            className="h-24 w-24 object-contain rounded-xl shadow-md bg-white p-2"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            onLoad={(e) => {
              e.target.style.display = 'block';
            }}
          />
        </div>
      </div>
    )}
  </div>
 </CardContent>
 </Card>

 {/* Job Details */}
 <Card>
 <CardHeader>
 <CardTitle>Job Details</CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-2">
 <Label>Job Title</Label>
 <Input {...register("title")} placeholder="e.g. Senior React Developer" />
 {errors.title && (
 <p className="text-sm text-red-500">{errors.title.message}</p>
 )}
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label>Category</Label>
 <Input {...register("category")} placeholder="e.g. Engineering" />
 {errors.category && (
 <p className="text-sm text-red-500">{errors.category.message}</p>
 )}
 </div>
 <div className="space-y-2">
 <Label>Location</Label>
 <Input {...register("location")} placeholder="e.g. Remote, New York" />
 {errors.location && (
 <p className="text-sm text-red-500">{errors.location.message}</p>
 )}
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label>Job Type</Label>
 <Select
 onValueChange={(val) => setValue("type", val)}
 defaultValue="FULL_TIME"
 >
 <SelectTrigger>
 <SelectValue placeholder="Select type" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="FULL_TIME">Full Time</SelectItem>
 <SelectItem value="PART_TIME">Part Time</SelectItem>
 <SelectItem value="CONTRACT">Contract</SelectItem>
 <SelectItem value="INTERNSHIP">Internship</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-2">
 <Label>Experience Level</Label>
 <Select
 onValueChange={(val) => setValue("experience", val)}
 defaultValue="ENTRY"
 >
 <SelectTrigger>
 <SelectValue placeholder="Select level" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="ENTRY">Entry Level</SelectItem>
 <SelectItem value="MID">Mid Level</SelectItem>
 <SelectItem value="SENIOR">Senior Level</SelectItem>
 <SelectItem value="EXECUTIVE">Executive</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
  <div className="space-y-2">
  <Label>Min Salary (ETB)</Label>
  <Input type="number" {...register("salaryMin")} placeholder="e.g. 50000" />
  </div>
  <div className="space-y-2">
  <Label>Max Salary (ETB)</Label>
  <Input type="number" {...register("salaryMax")} placeholder="e.g. 100000" />
  </div>
  <div className="space-y-2 sm:col-span-2 lg:col-span-1">
  <Label>Deadline</Label>
  <Input type="date" {...register("deadline")} className="w-full" />
  </div>
  </div>

 <div className="space-y-2">
 <Label>Tags (comma separated)</Label>
 <Input {...register("tags")} placeholder="React, Node.js, TypeScript" />
 </div>

 <div className="space-y-2">
 <Label>Description</Label>
 <Textarea
 {...register("description")}
 className="min-h-[150px] resize-none"
 placeholder="Detailed job description..."
 />
 {errors.description && (
 <p className="text-sm text-red-500">{errors.description.message}</p>
 )}
 </div>

 <div className="space-y-2">
 <Label>Requirements</Label>
 <Textarea
 {...register("requirements")}
 className="min-h-[100px] resize-none"
 placeholder="Key requirements..."
 />
 </div>
 </CardContent>
 </Card>

 <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate("/admin/dashboard")}
 className="w-full sm:w-auto min-h-[50px] rounded-xl"
 >
 Cancel
 </Button>
 <Button type="submit" disabled={loading} className="w-full sm:w-auto min-w-[140px] min-h-[50px] rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-600/20 text-white font-bold">
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 animate-spin" />
 Posting...
 </>
 ) : (
 "Post Job"
 )}
 </Button>
 </div>
 </form>
 </div>
 </div>
 );
}
